import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { WriteRequestOptions } from '../../requestOptions';
import type { ExpansionAssignmentRun } from './types';

const validate = (runId: number, subscriptionId: number, productIds?: number[]) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (productIds !== undefined && productIds.length === 0) {
    throw new Error('Select at least one product to assign');
  }
};

type RequestParams = {
  runId: number;
  /**
   * Any subscription of the organization — the API authorises the caller against it and scopes
   * every read by organization, never by this id.
   */
  subscriptionId: number;
  /**
   * Omitted means "every ready product", which is the API's own default — an explicit list of the
   * ready ids would freeze a selection the client never made and hide products the run found after
   * the page was rendered.
   */
  productIds?: number[];
  /**
   * The user's display identifier (e.g. "Alice Smith" or "alice@example.com"), passed from the
   * frontend to record who authorized the assignment. Optional — omitted if unavailable.
   */
  acceptedBy?: string;
};

export type AssignRunResponse = { assignment_run: ExpansionAssignmentRun };

export const assignRun = async (
  scope: RequestScope,
  { runId, subscriptionId, productIds, acceptedBy }: RequestParams,
  options: WriteRequestOptions
): Promise<ChannelApiResponse<AssignRunResponse>> => {
  try {
    validate(runId, subscriptionId, productIds);

    const requestBody: Record<string, unknown> = productIds === undefined ? {} : { product_ids: productIds };
    if (acceptedBy) {
      requestBody.accepted_by = acceptedBy;
    }

    const result = await scopedChannelApi(scope).post<AssignRunResponse>(`atts/expansion/runs/${runId}/assign`, requestBody, {
      ...options,
      headers: {
        'x-astur-subscription-id': subscriptionId.toString(),
      },
    });

    return {
      data: result.data,
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
